/**
 * How a client's In Transport message goes out. `sent` lets SENT pick per phone number: WhatsApp
 * when the number has it, otherwise SMS.
 */
export type MessageChannel = "sms" | "whatsapp" | "sent";

/** Request value that resets a client's channel or template to the deployment default. */
export const DEFAULT_SETTING = "default";
export type DefaultSetting = typeof DEFAULT_SETTING;

/** One client's settings — `GET api/client-messaging-settings`. */
export interface ClientMessagingSettings {
	sellerId: number;
	/** The buyer gets a message when an order goes In Transport, and the client is charged for it. */
	isMessagingEnabled: boolean;
	/** Charged per message at settlement, in the deployment's own currency. Null when never set. */
	messageCost: number | null;
	/** The driver must collect the PIN on delivery. Only applies while messaging is on. */
	requiresDeliveryPin: boolean;
	/** Null = the deployment default. */
	messageChannel: MessageChannel | null;
	/** SENT template id; null = the deployment default for the client's PIN choice. */
	messageTemplateId: string | null;
	/** Hide the price on the client's domestic tickets. Only applies while messaging is on. */
	hideOrderPriceOnTicket: boolean;
}

/** A SENT template an admin may assign to a client. */
export interface ClientMessageTemplate {
	id: string;
	name: string | null;
	language: string | null;
	/** The channels the template can be sent on, e.g. `["sms", "whatsapp"]`. */
	channels: string[];
	variables: string[];
	/** Carries the delivery PIN — only for clients with the PIN on. */
	includesPin: boolean;
	isApproved: boolean;
}

/** What this deployment offers — `GET api/client-messaging-settings/options`. */
export interface ClientMessagingOptions {
	/** False where only the legacy providers exist: no channel, no template, no "without PIN". */
	isSentEnabled: boolean;
	/** The channel a client without its own uses. */
	defaultChannel: MessageChannel;
	channels: MessageChannel[];
	templates: ClientMessageTemplate[];
	defaultTemplateWithPinId: string | null;
	/** Null until an approved template without the PIN is set up. */
	defaultTemplateWithoutPinId: string | null;
}

/**
 * One change applied to every listed client. Omitted fields are left as they are; `default` resets
 * the channel or the template to the deployment default.
 */
export interface UpdateClientMessagingSettingsRequest {
	sellerIds: number[];
	isMessagingEnabled?: boolean;
	messageCost?: number;
	requiresDeliveryPin?: boolean;
	hideOrderPriceOnTicket?: boolean;
	messageChannel?: MessageChannel | DefaultSetting;
	messageTemplateId?: string;
}

export interface UpdateClientMessagingSettingsResponse {
	updated: number;
	settings: ClientMessagingSettings[];
}

/** 400 body: nothing was saved; `sellers` lists why each client couldn't take the change. */
export interface ClientMessagingSettingsError {
	message: string;
	sellers: { sellerId: number; errors: string[] }[];
}
